import React, { useState } from "react"
import {
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Paper,
  Chip,
} from "@material-ui/core"
import { CheckCircle, RadioButtonUnchecked, Cancel } from "@material-ui/icons"
import styles from "./ApplicationWorkflow.module.scss"

const workflowSteps = [
  { key: "initial_review", label: "Initial Review" },
  { key: "verification", label: "Verification" },
  { key: "final_approval", label: "Final Approval" },
]

const ApplicationWorkflow = ({ application, onStatusTransition, loading }) => {
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState("")
  const [notes, setNotes] = useState("")
  const [transitioning, setTransitioning] = useState(false)

  const getCurrentStepIndex = () => {
    const statusMap = {
      pending: -1,
      initial_review: 0,
      verification: 1,
      final_approval: 2,
      approved: 3,
      rejected: -1,
      request_details: -1,
      request_details_applied: -1,
      under_review: 0,
    }
    return statusMap[application?.status] ?? -1
  }

  const getStepStatus = (stepIndex) => {
    const currentStep = getCurrentStepIndex()
    if (stepIndex < currentStep) return "completed"
    if (stepIndex === currentStep) return "active"
    return "pending"
  }

  const handleTransition = (status) => {
    setSelectedStatus(status)
    setOpenDialog(true)
  }

  const handleConfirmTransition = async () => {
    setTransitioning(true)
    try {
      await onStatusTransition(selectedStatus, notes)
      setOpenDialog(false)
      setNotes("")
      setSelectedStatus("")
    } catch (error) {
      console.error("Error transitioning status:", error)
    } finally {
      setTransitioning(false)
    }
  }

  const handleCancel = () => {
    setOpenDialog(false)
    setNotes("")
    setSelectedStatus("")
  }

  const getStatusColor = (status) => {
    const statusColors = {
      pending: "default",
      initial_review: "primary",
      verification: "primary",
      final_approval: "primary",
      approved: "primary",
      rejected: "secondary",
      request_details: "default",
    }
    return statusColors[status] || "default"
  }

  const availableTransitions = application?.available_transitions || []

  return (
    <div className={styles.workflowContainer}>
      <Typography variant="h6" className={styles.workflowTitle}>
        Review Workflow
      </Typography>

      <Paper className={styles.currentStatusPaper}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="body2" className={styles.currentStatusLabel}>
              Current Status
            </Typography>
            <Chip
              label={application?.status?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
              color={getStatusColor(application?.status)}
              className={styles.statusChip}
            />
          </Box>
          {application?.status === "approved" && (
            <CheckCircle className={styles.completedIcon} />
          )}
          {application?.status === "rejected" && (
            <Cancel className={styles.rejectedIcon} />
          )}
        </Box>
      </Paper>

      <Stepper activeStep={getCurrentStepIndex()} orientation="vertical" className={styles.stepper}>
        {workflowSteps.map((step, index) => {
          const stepStatus = getStepStatus(index)
          const isCompleted = stepStatus === "completed"
          const isActive = stepStatus === "active"

          return (
            <Step key={step.key} completed={isCompleted} active={isActive}>
              <StepLabel
                StepIconComponent={() => (
                  <div className={styles.stepIcon}>
                    {isCompleted ? (
                      <CheckCircle className={styles.completedIcon} />
                    ) : isActive ? (
                      <RadioButtonUnchecked className={styles.activeIcon} />
                    ) : (
                      <RadioButtonUnchecked className={styles.pendingIcon} />
                    )}
                  </div>
                )}
              >
                <Typography variant="h6" className={styles.stepLabel}>
                  {step.label}
                </Typography>
              </StepLabel>
              <StepContent>
                {isActive && availableTransitions.includes(step.key) && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleTransition(step.key)}
                    disabled={loading || transitioning}
                    className={styles.transitionButton}
                  >
                    Move to {step.label}
                  </Button>
                )}
                {isCompleted && (
                  <Typography variant="body2" className={styles.completedText}>
                    ✓ Completed
                  </Typography>
                )}
              </StepContent>
            </Step>
          )
        })}
      </Stepper>

      {availableTransitions.length > 0 && (
        <Box className={styles.quickActions}>
          <Typography variant="subtitle2" className={styles.quickActionsTitle}>
            Quick Actions
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {availableTransitions.map((status) => {
              if (workflowSteps.find((s) => s.key === status)) return null // Skip workflow steps, they're in stepper
              return (
                <Button
                  key={status}
                  variant="outlined"
                  size="small"
                  onClick={() => handleTransition(status)}
                  disabled={loading || transitioning}
                  className={styles.quickActionButton}
                >
                  {status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                </Button>
              )
            })}
          </Box>
        </Box>
      )}

      {/* Status History */}
      {application?.status_history && application.status_history.length > 0 && (
        <Box className={styles.statusHistory}>
          <Typography variant="h6" className={styles.historyTitle}>
            Status History
          </Typography>
          <div className={styles.historyList}>
            {application.status_history.map((history, index) => (
              <Paper key={index} className={styles.historyItem}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body1" className={styles.historyStatus}>
                      {history.status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                    </Typography>
                    {history.changed_by && (
                      <Typography variant="body2" className={styles.historyChangedBy}>
                        by {history.changed_by.name} ({history.changed_by.email})
                      </Typography>
                    )}
                    {history.notes && (
                      <Typography variant="body2" className={styles.historyNotes}>
                        {history.notes}
                      </Typography>
                    )}
                    <Typography variant="caption" className={styles.historyDate}>
                      {new Date(history.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </div>
        </Box>
      )}

      {/* Transition Dialog */}
      <Dialog open={openDialog} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Transition Application Status</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to move this application to{" "}
            <strong>
              {selectedStatus.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
            </strong>
            ?
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Notes (optional)"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            variant="outlined"
            className={styles.notesField}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="secondary" disabled={transitioning}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmTransition}
            color="primary"
            variant="contained"
            disabled={transitioning}
          >
            {transitioning ? "Transitioning..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default ApplicationWorkflow

